import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Chip, Button, TextField, Snackbar, Alert } from "@mui/material";
import { getUserDetail, updatePhoneNumberId } from "../api/adminApi";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useParams, useNavigate } from "react-router-dom";

const statusColors = {
  pending: "#f59e0b", payment_pending: "#f59e0b", onboarding: "#3b82f6",
  details_submitted: "#8b5cf6", otp_ready: "#3b82f6", otp_verified: "#22c55e",
  active: "#22c55e", suspended: "#ef4444",
};

const statusLabels = {
  pending: "Pending Approval", payment_pending: "Payment Needed", onboarding: "Setting Up",
  details_submitted: "Details Submitted", otp_ready: "OTP Ready", otp_verified: "OTP Verified",
  active: "Active", suspended: "Suspended",
};

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bots, setBots] = useState([]);
  const [waAccount, setWaAccount] = useState(null);
  const [conversationCount, setConversationCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [phoneId, setPhoneId] = useState("");
  const [wabaId, setWabaId] = useState("");
  const [clientToken, setClientToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const fetchUserDetail = async () => {
    setLoading(true);
    try {
      const data = await getUserDetail(id);
      setUser(data.user);
      setBots(data.bots || []);
      setConversationCount(data.conversationCount || 0);
      setWaAccount(data.waAccount || null);
      setPhoneId(data.waAccount?.phoneNumberId || "");
    } catch (err) {
      setSnack({ open: true, message: "Failed to load user details", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchUserDetail();
  }, [id]);

  const handleSavePhoneId = async () => {
    if (!phoneId.trim()) {
      setSnack({ open: true, message: "Phone Number ID is required", severity: "warning" });
      return;
    }
    setSaving(true);
    try {
      await updatePhoneNumberId(id, { phoneNumberId: phoneId.trim(), wabaId: wabaId.trim(), clientAccessToken: clientToken.trim() });
      setSnack({ open: true, message: "Connection saved! Client is now active.", severity: "success" });
      fetchUserDetail();
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
    setSaving(false);
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  if (!user) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><Typography color="#64748b">User not found</Typography></Box>;

  const isWaClient = waAccount && waAccount.onboardingStatus;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}>
        <Typography fontWeight={800} fontSize={24} color="#1e293b">User Details</Typography>
        <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate("/users")}
          sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
          Back to Users
        </Button>
      </Box>

      <Paper className="glass-card" sx={{ p: 3.5, mb: 5 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {[
            { label: "Full Name", value: user.fullName || "N/A" },
            { label: "Email", value: user.email },
            { label: "Role", value: user.role, color: user.role === "admin" ? "#ef4444" : user.role === "supervisor" ? "#f59e0b" : user.role === "analyst" ? "#6366f1" : user.role === "agent" ? "#10b981" : "#64748b" },
            { label: "Plan", value: user.planName || "None", color: user.planName ? "#10b981" : "#64748b" },
            { label: "Joined", value: new Date(user.createdAt).toLocaleDateString() },
          ].map((f, i) => (
            <Box key={i} sx={{ minWidth: 140 }}>
              <Typography fontWeight={600} fontSize={12} color="#64748b" textTransform="uppercase">{f.label}</Typography>
              <Typography fontWeight={700} fontSize={18} color={f.color || "#1e293b"} sx={{ textTransform: "capitalize" }}>{f.value}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      <Box sx={{ display: "flex", gap: 4, mb: 5, flexWrap: "wrap" }}>
        <Box sx={{ minWidth: 150 }}>
          <Typography fontWeight={600} fontSize={12} color="#64748b" textTransform="uppercase">Bots Owned</Typography>
          <Typography fontWeight={700} fontSize={20} color="#1e293b">{bots.length}</Typography>
        </Box>
        <Box sx={{ minWidth: 150 }}>
          <Typography fontWeight={600} fontSize={12} color="#64748b" textTransform="uppercase">Conversations</Typography>
          <Typography fontWeight={700} fontSize={20} color="#1e293b">{conversationCount}</Typography>
        </Box>
      </Box>

      {isWaClient && (
        <Paper className="glass-card" sx={{ p: 3.5, mb: 5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <WhatsAppIcon sx={{ color: "#25d366", fontSize: 24 }} />
            <Typography fontWeight={700} fontSize={18} color="#1e293b">WhatsApp Onboarding</Typography>
            <Chip label={statusLabels[waAccount.onboardingStatus] || waAccount.onboardingStatus} size="small"
              sx={{ ml: 1, color: "#fff", bgcolor: statusColors[waAccount.onboardingStatus] || "#64748b", fontWeight: 600, fontSize: 12 }} />
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 3 }}>
            {[
              { label: "WhatsApp Number", value: waAccount.whatsappBusinessNumber || "—" },
              { label: "Business Name", value: waAccount.businessName || "—" },
              { label: "Website", value: waAccount.website || "—" },
              { label: "Industry", value: waAccount.industryCategory || "—" },
              { label: "Company", value: waAccount.companyName || "—" },
              { label: "Mobile", value: waAccount.contactMobile || "—" },
              { label: "Client BM ID", value: waAccount.facebookBusinessManagerId || "—" },
              { label: "WABA ID", value: waAccount.clientWabaId || waAccount.wabaId || "—" },
              ...(waAccount.twoFactorPin ? [{ label: "2FA PIN", value: waAccount.twoFactorPin }] : []),
            ].map((f, i) => (
              <Box key={i} sx={{ minWidth: 140 }}>
                <Typography fontWeight={600} fontSize={11} color="#64748b" textTransform="uppercase">{f.label}</Typography>
                <Typography fontWeight={600} color="#1e293b">{f.value}</Typography>
              </Box>
            ))}
          </Box>

          {(waAccount.onboardingStatus === "details_submitted") && (
            <Box>
              <Box sx={{ p: 2.5, bgcolor: "#f8faff", borderRadius: 2, border: "1px solid #22c55e40", mb: 2 }}>
                <Typography fontWeight={600} color="#22c55e" mb={1}>Option 1 (Recommended): Already Connected in WhatsApp Manager</Typography>
                <Typography variant="body2" color="#475569" mb={2}>
                  Number already exists in WhatsApp Manager (client's own BM). Paste Phone Number ID + WABA ID (from the SAME account), plus the client's system-user token if the number is in the client's Business Manager.
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                  <TextField label="Phone Number ID" value={phoneId} onChange={(e) => setPhoneId(e.target.value)}
                    fullWidth size="small" placeholder="e.g. 1191938384004414"
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", color: "#1e293b", "& fieldset": { borderColor: "#e2e8f0" } }, "& .MuiInputLabel-root": { color: "#64748b" } }} />
                  <TextField label="WABA ID" value={wabaId} onChange={(e) => setWabaId(e.target.value)}
                    fullWidth size="small" placeholder="Same account as the number"
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", color: "#1e293b", "& fieldset": { borderColor: "#e2e8f0" } }, "& .MuiInputLabel-root": { color: "#64748b" } }} />
                  <TextField label="Client Access Token (optional)" value={clientToken} onChange={(e) => setClientToken(e.target.value)}
                    fullWidth size="small" placeholder="System user token from client's BM"
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", color: "#1e293b", "& fieldset": { borderColor: "#e2e8f0" } }, "& .MuiInputLabel-root": { color: "#64748b" } }} />
                </Box>
                <Button variant="contained" onClick={handleSavePhoneId} disabled={saving || !phoneId.trim()}
                  sx={{ bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" }, textTransform: "none", borderRadius: 2, px: 3, minWidth: 140 }}>
                  {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save & Activate"}
                </Button>
              </Box>
            </Box>
          )}

          {waAccount.onboardingStatus === "otp_ready" && (
            <Box sx={{ p: 2.5, bgcolor: "#f8faff", borderRadius: 2, border: "1px solid #60a5fa40" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CircularProgress size={24} sx={{ color: "#6366f1" }} />
                <Box>
                  <Typography fontWeight={600} color="#6366f1">Number Registered — Waiting for Client OTP</Typography>
                  <Typography variant="body2" color="#475569">WABA ID: {waAccount.clientWabaId || waAccount.wabaId} | Phone Number ID: {waAccount.phoneNumberId}. Client will enter OTP on their dashboard.</Typography>
                </Box>
              </Box>
            </Box>
          )}

          {waAccount.onboardingStatus === "otp_verified" && (
            <Box sx={{ p: 2.5, bgcolor: "#f0fdf4", borderRadius: 2, border: "1px solid #22c55e40" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 28 }} />
                <Box>
                  <Typography fontWeight={600} color="#22c55e">Phone Number Verified — Waiting for Admin Activation</Typography>
                  <Typography variant="body2" color="#475569">Phone Number ID: {waAccount.phoneNumberId}. Client is ready, click Verify in WhatsApp tab.</Typography>
                </Box>
              </Box>
            </Box>
          )}

          {(waAccount.onboardingStatus === "otp_verified" || waAccount.onboardingStatus === "active") && (
            <Box sx={{ p: 2.5, bgcolor: "#f0fdf4", borderRadius: 2, border: "1px solid #22c55e40" }}>
              <Typography fontWeight={600} color="#22c55e">✓ Phone Number Active</Typography>
              <Typography variant="body2" color="#475569">Phone Number ID: {waAccount.phoneNumberId}</Typography>
            </Box>
          )}
        </Paper>
      )}

      <Paper className="glass-card" sx={{ p: 3.5 }}>
        <Typography fontWeight={600} color="#1e293b" mb={2}>Bots Owned ({bots.length})</Typography>
        {bots.length === 0 ? (
          <Typography color="#64748b" sx={{ textAlign: "center", py: 4 }}>No bots found</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Bot Name</TableCell>
                  <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Status</TableCell>
                  <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bots.map((bot) => (
                  <TableRow key={bot._id} hover sx={{ "&:hover": { bgcolor: "#f8faff" }, "&:last-child td": { border: 0 } }}>
                    <TableCell sx={{ borderColor: "#e2e8f0" }}><Typography fontWeight={600} color="#1e293b">{bot.name}</Typography></TableCell>
                    <TableCell sx={{ borderColor: "#e2e8f0" }}>
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.3, borderRadius: 1, bgcolor: bot.status === "active" ? "#10b98112" : "#64748b12" }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: bot.status === "active" ? "#10b981" : "#64748b" }} />
                        <Typography variant="caption" fontWeight={600} color={bot.status === "active" ? "#10b981" : "#64748b"}>{bot.status.charAt(0).toUpperCase() + bot.status.slice(1)}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderColor: "#e2e8f0" }}><Typography variant="body2" color="#1e293b">{new Date(bot.createdAt).toLocaleDateString()}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: "100%", borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default UserDetail;
